import { useCallback, useEffect, useState } from 'react';
import debounce from '@helpers/debounce';

interface UseScrollShadowReturn {
  showTopShadow: boolean;
  showBottomShadow: boolean;
  setRef: (node: HTMLElement | null) => void;
}

const useScrollShadow = (): UseScrollShadowReturn => {
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const debounceMs = 250;

  const handleShadows = useCallback(() => {
    if (!element) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    setShowTopShadow(scrollTop > 0);
    setShowBottomShadow(scrollTop + clientHeight < scrollHeight);
  }, [element]);

  const setRef = useCallback((node: HTMLElement | null) => {
    if (node) {
      setElement(node);
    }
  }, []);

  useEffect(() => {
    if (!element) return;

    const scrollHandler = debounce(() => {
      handleShadows();
    }, debounceMs);

    const resizeHandler = debounce(() => {
      handleShadows();
    }, debounceMs);

    element.addEventListener('scroll', scrollHandler);

    const resizeObserver = new ResizeObserver(resizeHandler);
    resizeObserver.observe(element);

    const mutationObserver = new MutationObserver(() => {
      handleShadows();
    });
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
    });

    handleShadows();

    return () => {
      element.removeEventListener('scroll', scrollHandler);
      scrollHandler.cancel?.();
      resizeHandler.cancel?.();
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [element, handleShadows]);

  return {
    showTopShadow,
    showBottomShadow,
    setRef,
  };
};

export default useScrollShadow;
